/**
 * App Message Types
 *
 * Types for the application message system (messages sent by tenant applications to users).
 * This is distinct from the platform inbox system (tenant-level notifications).
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Message priority level
 */
export type MessagePriority = 'LOW' | 'MEDIUM' | 'HIGH';

// ============================================================================
// CHANNEL CONFIGURATION
// ============================================================================

/**
 * Message delivery channels configuration
 */
export interface MessageChannels {
  /** Store in user's inbox (always enabled) */
  inbox: boolean;
  /** Send push notification alert */
  push: boolean;
}

// ============================================================================
// SENDER TYPES
// ============================================================================

/**
 * Message sender information (for UI display)
 */
export interface MessageSender {
  /** Sender type (always APPLICATION for app messages) */
  type: 'APPLICATION';
  /** Application UUID */
  applicationId: string;
  /** Application code (slug) */
  applicationCode: string;
  /** Application display name */
  applicationName: string;
}

// ============================================================================
// APP MESSAGE
// ============================================================================

/**
 * Sanitized app message (for API responses)
 * Excludes DynamoDB internal keys
 */
export interface AppMessageSanitized {
  // Entity metadata
  entityType: 'APP_MESSAGE';
  createdAt: string;
  updatedAt: string;

  // Identity
  messageId: string;
  tenantId: string;
  applicationId: string;
  applicationCode: string;
  applicationName: string;
  userId: string;

  // Message Content
  /** App-defined message type (e.g., ORDER_SHIPPED, NEW_COMMENT) */
  type: string;
  /** Message priority level */
  priority: MessagePriority;
  /** Message subject/title (max 200 chars) */
  subject: string;
  /** Message body (max 10,000 chars, markdown supported) */
  body: string;

  // Optional Fields
  /** Custom metadata for app use (deep linking, etc.) */
  data?: Record<string, unknown>;
  /** Action URL for deep linking */
  actionUrl?: string;

  // Sender Info (for UI display)
  sender: MessageSender;

  // Channel Configuration
  channels: MessageChannels;

  // Status
  isRead: boolean;
  isArchived: boolean;
  isDeleted: boolean;
  deletedAt?: string;

  // TTL for automatic cleanup (optional)
  expiresAt?: number; // Unix timestamp
}

// ============================================================================
// UNIFIED INBOX TYPES
// ============================================================================

/**
 * Source filter for unified inbox
 */
export type InboxSourceFilter = 'all' | 'platform' | 'applications';

/**
 * App message view type
 */
export type AppMessageView = 'inbox' | 'archived' | 'deleted';

/**
 * Unified inbox item combining platform inbox messages and app messages
 */
export interface UnifiedInboxItem {
  /** Source of the message */
  source: 'platform' | 'application';
  /** Unique identifier (messageId for both types) */
  id: string;
  /** Message subject/title */
  subject: string;
  /** Message body/preview */
  body: string;
  /** Priority level */
  priority: MessagePriority | 'LOW' | 'MEDIUM' | 'HIGH';
  /** Read status */
  isRead: boolean;
  /** Archive status */
  isArchived: boolean;
  /** Delete status */
  isDeleted: boolean;
  /** Deletion timestamp */
  deletedAt?: string;
  /** Creation timestamp */
  createdAt: string;
  /** Sender information */
  sender: {
    type: 'SYSTEM' | 'PLATFORM_ADMIN' | 'TENANT_ADMIN' | 'APPLICATION';
    name: string;
    applicationCode?: string; // Only for app messages
  };
  /** Optional action URL for deep linking */
  actionUrl?: string;
  /** Optional metadata */
  data?: Record<string, unknown>;
  /** Application ID (only for app messages) */
  applicationId?: string;
}

// ============================================================================
// QUERY/RESPONSE TYPES
// ============================================================================

/**
 * List messages query parameters
 */
export interface AppMessageListQuery {
  /** View type (inbox, archived, deleted) */
  view?: AppMessageView;
  /** Filter to unread messages only */
  unreadOnly?: boolean;
  /** Filter by message type */
  type?: string;
  /** Filter by priority */
  priority?: MessagePriority;
  /** Filter by date range start */
  dateStart?: string;
  /** Filter by date range end */
  dateEnd?: string;
  /** Pagination limit */
  limit?: number;
  /** Pagination cursor */
  cursor?: string;
}

/**
 * List messages response from Data Plane API
 */
export interface AppMessageListResponse {
  messages: AppMessageSanitized[];
  cursor?: string;
}

/**
 * Unread count response from Data Plane API
 */
export interface AppMessageUnreadCountResponse {
  count: number;
}

/**
 * Mark all as read response from Data Plane API
 */
export interface AppMessageMarkAllAsReadResponse {
  success: boolean;
  count: number;
}

// ============================================================================
// DISPLAY HELPERS
// ============================================================================

/**
 * Priority colors for UI display
 */
export const messagePriorityColors: Record<MessagePriority, string> = {
  LOW: 'bg-gray-100 text-gray-700',
  MEDIUM: 'bg-blue-100 text-blue-700',
  HIGH: 'bg-red-100 text-red-700',
};

/**
 * Priority labels for UI display
 */
export const messagePriorityLabels: Record<MessagePriority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};
